import { IconButton, LuIcon, Menu, stopPropagation } from "@overmap-ai/blocks"
import {
	useVideoViewer,
	VideoCard as OvermapVideoCard,
	type VideoCardProps as OvermapVideoCardProps,
} from "@overmap-ai/forms"
import { saveAs } from "file-saver"
import { memo, type MouseEventHandler, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"

export interface VideoCardProps extends Omit<OvermapVideoCardProps, "rightSlot"> {
	onDelete?: (file: File | null) => void
}

export const VideoCard = memo((props: VideoCardProps) => {
	const { file, error, onDelete, ...rest } = props

	const openVideoViewer = useVideoViewer()

	const handleView = useCallback(() => {
		if (!file) return

		openVideoViewer((close) => ({
			file: file,
			onClose: close,
		}))
	}, [file, openVideoViewer])

	const handleClick = useCallback<MouseEventHandler>(
		(e) => {
			e.stopPropagation()
			handleView()
		},
		[handleView],
	)

	const handleDownload = useCallback(() => {
		if (!file) return

		saveAs(file, file.name)
	}, [file])

	const handleDelete = useCallback(() => {
		onDelete?.(file)
	}, [file, onDelete])

	return (
		<OvermapVideoCard
			onClick={handleClick}
			file={file}
			error={error}
			size="sm"
			rightSlot={
				<div className="flex grow justify-end">
					<Menu.Root>
						<Menu.ClickTrigger>
							<IconButton variant="ghost" size="sm" accentColor="base" onClick={stopPropagation}>
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleView}>
								<LuIcon icon={modelIconMapping.expand} />
								View
							</Menu.Item>
							<Menu.Item onSelect={handleDownload} disabled={!file}>
								<LuIcon icon={modelIconMapping.download} />
								Download
							</Menu.Item>

							{onDelete ? (
								<>
									<Menu.Separator />
									<Menu.Item onSelect={handleDelete} disabled={!file}>
										<LuIcon icon={modelIconMapping.delete} />
										Delete
									</Menu.Item>
								</>
							) : null}
						</Menu.Content>
					</Menu.Root>
				</div>
			}
			{...rest}
		/>
	)
})
VideoCard.displayName = "VideoCard"
